import { DataTable } from "@/components/DataTable"
import { DetailShell } from "@/components/DetailShell"
import { Field } from "@/components/Field"
import { Link } from "@/components/Link"
import { Section } from "@/components/Section"
import { client } from "@/lib/client"
import { dash } from "@/lib/utils"
import { useApi, type Nav } from "@/lib/hooks"

export function CourtDetail({ id, nav }: { id: string; nav: Nav }) {
  const { data, err } = useApi(() => client.api.courts[":id"].$get({ param: { id } }), [id])
  const c = data?.court
  return (
    <DetailShell title={c ? dash(c.fullName) : "…"} backTo="/courts" nav={nav} data={data} err={err}>
      {data && c && (
        <div className="space-y-6">
          <div className="space-y-1">
            <Field label="ID" value={dash(c.id)} />
            <Field label="Short name" value={dash(c.shortName)} />
            <Field label="Citation" value={dash(c.citationString)} />
            <Field label="Jurisdiction" value={dash(c.jurisdiction)} />
            <Field
              label="Website"
              value={
                c.url ? (
                  <a className="text-primary hover:underline" href={c.url} target="_blank" rel="noreferrer">
                    {c.url}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            <Field label="Active" value={c.inUse ? "Yes" : "No"} />
            <Field label="Dates" value={`${dash(c.startDate)} – ${c.endDate ?? "present"}`} />
            <Field
              label="Parent court"
              value={
                data.parent ? (
                  <Link to={`/courts/${data.parent.id}`} nav={nav}>
                    {dash(data.parent.fullName)}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Field label="Cases" value={data.caseCount} />
            <Field label="Judges" value={data.judgeCount} />
            {c.notes && <Field label="Notes" value={c.notes} />}
          </div>

          <Section title="Courthouses">
            <DataTable
              headers={["Building", "Address", "City", "County", "State", "Zip", "Seat"]}
              rows={data.courthouses.map((r) => [
                dash(r.buildingName),
                dash([r.address1, r.address2].filter(Boolean).join(", ")),
                dash(r.city),
                dash(r.county),
                dash(r.state),
                dash(r.zipCode),
                r.courtSeat ? "Yes" : "No",
              ])}
            />
          </Section>

          <Section title="Sub-courts">
            <DataTable
              headers={["Court"]}
              rows={data.children.map((r) => [
                <Link to={`/courts/${r.id}`} nav={nav}>
                  {dash(r.fullName)}
                </Link>,
              ])}
            />
          </Section>

          <Section title="Appeals to">
            <DataTable
              headers={["Court"]}
              rows={data.appealsTo.map((r) => [
                <Link to={`/courts/${r.id}`} nav={nav}>
                  {dash(r.fullName)}
                </Link>,
              ])}
            />
          </Section>

          <Section title="Hears appeals from">
            <DataTable
              headers={["Court"]}
              rows={data.appealsFrom.map((r) => [
                <Link to={`/courts/${r.id}`} nav={nav}>
                  {dash(r.fullName)}
                </Link>,
              ])}
            />
          </Section>
        </div>
      )}
    </DetailShell>
  )
}
